'use client';

import { useMemo, useState } from 'react';
import Link from 'next/link';
import { motion, AnimatePresence } from 'framer-motion';
import { ExternalLink, ArrowRight } from 'lucide-react';
import { FaGithub } from 'react-icons/fa';
import { useSiteData } from '@/lib/dataCache';
import OptimizedImage from '@/components/ui/OptimizedImage';

const FILTERS = ['All', 'React', 'Next.js', 'Laravel', 'Full-Stack'] as const;
type Filter = (typeof FILTERS)[number];

type ApiProject = {
  id: string;
  title: string;
  slug: string;
  description: string;
  techStack: string[];
  coverImage?: string | null;
  images?: string[];
  liveUrl?: string | null;
  githubUrl?: string | null;
  featured: boolean;
  category: string;
};

// Deterministic gradient palette per project index for consistent SSR
const GRADIENTS = [
  'linear-gradient(135deg, #6C63FF 0%, #9F7AEA 50%, #FF6584 100%)',
  'linear-gradient(135deg, #4F46E5 0%, #7C3AED 50%, #EC4899 100%)',
  'linear-gradient(135deg, #06B6D4 0%, #6C63FF 50%, #8B5CF6 100%)',
  'linear-gradient(135deg, #FF6584 0%, #F97316 50%, #FACC15 100%)',
  'linear-gradient(135deg, #0EA5E9 0%, #6366F1 50%, #A855F7 100%)',
  'linear-gradient(135deg, #10B981 0%, #14B8A6 50%, #6C63FF 100%)',
];

function getInitials(title: string): string {
  const words = title
    .split(/\s+/)
    .filter((w) => /[a-zA-Z0-9]/.test(w))
    .filter((w) => !['a', 'an', 'the', 'of', 'and', '&'].includes(w.toLowerCase()));
  const letters = words.slice(0, 2).map((w) => w[0].toUpperCase());
  if (letters.length === 0) return title.slice(0, 2).toUpperCase();
  if (letters.length === 1) return letters[0] + (words[0][1] ?? '').toUpperCase();
  return letters.join('');
}

function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max).trimEnd() + '…';
}

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' as const } },
};

const gridContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.08, delayChildren: 0.05 },
  },
};

const cardVariant = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } },
};

export default function Projects() {
  const [filter, setFilter] = useState<Filter>('All');
  const projects = useSiteData<ApiProject[]>('projects', '/api/projects', []);

  const filtered = useMemo(() => {
    const featured = projects.filter((p) => p.featured);
    const base =
      filter === 'All'
        ? featured
        : featured.filter((p) => {
            if (p.category === filter) return true;
            if (filter === 'React') {
              return p.techStack.some((t) => t.toLowerCase() === 'react');
            }
            if (filter === 'Next.js') {
              return p.techStack.some((t) => t.toLowerCase() === 'next.js');
            }
            if (filter === 'Laravel') {
              return p.techStack.some((t) => t.toLowerCase() === 'laravel');
            }
            return false;
          });
    return base.slice(0, 6);
  }, [filter, projects]);

  return (
    <section id="projects" className="projects-section">
      <div className="projects-blob projects-blob-1" />
      <div className="projects-blob projects-blob-2" />

      <div className="section-container">
        <motion.div
          className="section-heading"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={fadeUp}
        >
          <span className="section-label">Featured Projects</span>
          <h2 className="section-title">Selected Work</h2>
          <p className="section-subtitle">
            A snapshot of recent projects I&apos;ve designed and built — from
            full-stack platforms to focused product experiences.
          </p>
        </motion.div>

        {/* Filter tabs */}
        <motion.div
          className="projects-filters"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          {FILTERS.map((f) => (
            <button
              key={f}
              type="button"
              onClick={() => setFilter(f)}
              className={`projects-filter ${filter === f ? 'projects-filter-active' : ''}`}
              aria-pressed={filter === f}
            >
              {f}
            </button>
          ))}
        </motion.div>

        {/* Grid */}
        <AnimatePresence mode="wait">
          <motion.div
            key={filter}
            className="projects-grid"
            variants={gridContainer}
            initial="hidden"
            animate="visible"
            exit="hidden"
          >
            {filtered.length === 0 && projects.length > 0 && (
              <div className="projects-empty">
                No projects match this filter yet. Check back soon.
              </div>
            )}

            {filtered.map((project, i) => {
              const gradient = GRADIENTS[i % GRADIENTS.length];
              const initials = getInitials(project.title);
              const cover =
                project.coverImage ||
                (project.images && project.images.length > 0 ? project.images[0] : null);
              return (
                <motion.article
                  key={project.id}
                  className="project-card"
                  variants={cardVariant}
                  whileHover={{ y: -8 }}
                >
                  {/* Header */}
                  <div className="project-card-header" style={{ background: gradient }}>
                    {cover ? (
                      <OptimizedImage
                        src={cover}
                        alt={project.title}
                        className="projects-card-img"
                        style={{ objectFit: 'cover', width: '100%', height: '100%' }}
                        loading="lazy"
                      />
                    ) : (
                      <>
                        <div className="project-card-pattern" />
                        <span className="project-card-initials">{initials}</span>
                      </>
                    )}
                    <span className="project-card-category-badge">{project.category}</span>

                    {/* Hover overlay */}
                    <div className="project-card-overlay">
                      {project.liveUrl && (
                        <a
                          href={project.liveUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-card-overlay-btn project-card-overlay-live"
                          aria-label={`View ${project.title} live`}
                        >
                          <ExternalLink size={16} />
                          <span>Live Demo</span>
                        </a>
                      )}
                      {project.githubUrl && (
                        <a
                          href={project.githubUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="project-card-overlay-btn project-card-overlay-github"
                          aria-label={`View ${project.title} source on GitHub`}
                        >
                          <FaGithub size={16} />
                          <span>Source</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Body */}
                  <div className="project-card-body">
                    <Link href={`/projects/${project.slug}`} className="project-card-title-link">
                      <h3 className="project-card-title">{project.title}</h3>
                    </Link>

                    <p className="project-card-description">
                      {truncateText(project.description, 110)}
                    </p>

                    <div className="project-card-tech">
                      {project.techStack.slice(0, 4).map((tech) => (
                        <span key={tech} className="project-card-tech-pill">
                          {tech}
                        </span>
                      ))}
                      {project.techStack.length > 4 && (
                        <span className="project-card-tech-pill project-card-tech-more">
                          +{project.techStack.length - 4}
                        </span>
                      )}
                    </div>

                    <Link
                      href={`/projects/${project.slug}`}
                      className="project-card-link"
                    >
                      View Case Study
                      <ArrowRight size={14} />
                    </Link>
                  </div>
                </motion.article>
              );
            })}
          </motion.div>
        </AnimatePresence>

        {/* View all */}
        <motion.div
          className="projects-cta"
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <Link href="/projects" className="btn btn-primary">
            View All Projects
            <ArrowRight size={18} />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
